import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';

const fmt  = n => Number(n || 0).toLocaleString() + ' so\'m';
const fmtN = n => Number(n || 0).toLocaleString();
const pct  = n => (Number(n || 0)).toFixed(1) + '%';

const getTabs = (tr) => [
  { key: 'revenue',   label: `📈 ${tr('revenue')}` },
  { key: 'shifts',    label: `🗒 ${tr('tab_shifts')}` },
  { key: 'pl',        label: '💼 P&L' },
  { key: 'customers', label: `👥 ${tr('topCustomers')}` },
  { key: 'occupancy', label: `🔥 ${tr('activeSessions')}` },
  { key: 'anomalies', label: `⚠️ ${tr('suspiciousActivity')}` },
];

function Tab({ active, onClick, label }) {
  return (
    <button onClick={onClick} style={{
      padding: '9px 20px', borderRadius: 10, fontSize: 13, cursor: 'pointer', fontWeight: 600,
      background: active ? 'rgba(0,212,255,0.12)' : 'transparent',
      border: `1px solid ${active ? 'var(--cyan)' : 'var(--border)'}`,
      color: active ? 'var(--cyan)' : 'var(--text2)',
    }}>{label}</button>
  );
}

function StatCard({ icon, value, label, color = 'var(--cyan)', sub }) {
  return (
    <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14,
      padding: '20px 24px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: -15, right: -15, width: 70, height: 70,
        borderRadius: '50%', background: color, filter: 'blur(28px)', opacity: 0.3 }} />
      <div style={{ fontSize: 24, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 20, fontWeight: 700, color, marginBottom: 2 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text2)' }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function PeriodPicker({ from, to, setFrom, setTo }) {
  const { t: tr } = useLang();
  const preset = (days) => {
    const now = new Date(); const f = new Date(now.getTime() - days * 86400000);
    setFrom(f.toLocaleDateString('sv-SE')); setTo(now.toLocaleDateString('sv-SE'));
  };
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      {[
        { labelKey: 'today',   label: 'Bugun',  days: 0 },
        { labelKey: 'last7days', label: '7 kun', days: 7 },
        { labelKey: 'last30days', label: '30 kun', days: 30 },
        { labelKey: 'thisMonth', label: 'Bu oy', days: null },
      ].map(({ labelKey, label, days }) => (
        <button key={label} onClick={() => {
          if (days === null) {
            const now = new Date();
            setFrom(new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString('sv-SE'));
            setTo(now.toLocaleDateString('sv-SE'));
          } else {
            preset(days);
          }
        }} style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
          background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text2)' }}>
          {tr(labelKey) || label}
        </button>
      ))}
      <input type="date" value={from} onChange={e => setFrom(e.target.value)}
        style={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)',
          padding: '6px 10px', borderRadius: 8, fontSize: 12, outline: 'none' }} />
      <span style={{ color: 'var(--text3)' }}>—</span>
      <input type="date" value={to} onChange={e => setTo(e.target.value)}
        style={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)',
          padding: '6px 10px', borderRadius: 8, fontSize: 12, outline: 'none' }} />
    </div>
  );
}

// ── DAROMAD TAB ───────────────────────────────────────────────────────────
function RevenueTab() {
  const { t: tr } = useLang();
  const now = new Date();
  const [from, setFrom] = useState(new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString('sv-SE'));
  const [to, setTo]     = useState(now.toLocaleDateString('sv-SE'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/finance/revenue?from=${from}&to=${to}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    window.open(`${api.defaults.baseURL}/finance/export/revenue?from=${from}&to=${to}`, '_blank');
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{tr('loading')}</div>;
  if (!data) return null;

  const t        = data.totals || {};
  const snack    = data.snack  || {};
  const session  = data.session_spending || {};
  const topupTotal = t.topup || 0;
  const refundsTotal = t.refunds || 0;
  const grossTopup   = t.gross_topup || 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <PeriodPicker from={from} to={to} setFrom={setFrom} setTo={setTo} />
        <button onClick={exportCsv} style={{ padding: '8px 18px', borderRadius: 9, fontSize: 12, cursor: 'pointer',
          background: 'rgba(0,255,136,0.1)', border: '1px solid var(--green)', color: 'var(--green)', fontWeight: 600 }}>
          📥 CSV Eksport
        </button>
      </div>

      {/* Jami kartalar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
        <StatCard icon="💰" value={fmt(t.total)}        label={tr('totalRevenue')} color="var(--green)"
          sub={`Topup + ${tr('snackSales')}`} />
        <StatCard icon="💳" value={fmt(t.topup)}         label="Sof daromad (vazvratsiz)" color="var(--cyan)"
          sub={grossTopup > 0 ? `Brutto: ${fmt(grossTopup)}` : undefined} />
        <StatCard icon="🍕" value={fmt(snack.revenue)}   label={tr('totalSales')}  color="var(--yellow)"
          sub={Number(snack.returns_amount || 0) > 0
            ? `${snack.order_count || 0} · ↩ −${fmt(snack.returns_amount)}`
            : `${snack.order_count || 0}`} />
        <StatCard icon="📈" value={fmt(snack.profit)}    label={tr('margin')}      color="var(--purple)"
          sub={`${pct(snack.margin_pct)}`} />
      </div>

      {/* Vazvrat bloki */}
      {refundsTotal > 0 && (
        <div style={{
          background: 'rgba(255,59,92,0.06)', border: '1px solid rgba(255,59,92,0.3)',
          borderRadius: 12, padding: '14px 20px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>↩ Balans vazvrat</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--red)' }}>−{fmt(refundsTotal)}</div>
          </div>
          <div style={{ color: 'var(--text3)', fontSize: 12 }}>→</div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>Brutto topup</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text2)' }}>{fmt(grossTopup)}</div>
          </div>
          <div style={{ color: 'var(--text3)', fontSize: 12 }}>−{fmt(refundsTotal)} =</div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>Sof daromad</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--cyan)' }}>{fmt(topupTotal)}</div>
          </div>
        </div>
      )}

      {/* Bar vazvrat bloki */}
      {Number(snack.returns_amount || 0) > 0 && (
        <div style={{
          background: 'rgba(255,193,7,0.06)', border: '1px solid rgba(255,193,7,0.3)',
          borderRadius: 12, padding: '14px 20px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>↩ Bar vazvrat</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--red)' }}>−{fmt(snack.returns_amount)}</div>
          </div>
          <div style={{ color: 'var(--text3)', fontSize: 12 }}>→</div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>Yalpi bar savdo</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text2)' }}>{fmt((snack.revenue || 0) + (snack.returns_amount || 0))}</div>
          </div>
          <div style={{ color: 'var(--text3)', fontSize: 12 }}>−{fmt(snack.returns_amount)} =</div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>Sof bar savdo</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--yellow)' }}>{fmt(snack.revenue)}</div>
          </div>
        </div>
      )}


      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Topup manbalari */}
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 11,
            color: 'var(--text2)', letterSpacing: 1.5, textTransform: 'uppercase' }}>
            💳 Pul kirimi (topup) — manba bo'yicha
          </div>
          {!data.sources?.length ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)' }}>{tr('noTopup')}</div>
          ) : data.sources.map((s, i) => {
            const pctVal = topupTotal > 0 ? (s.revenue / topupTotal * 100) : 0;
            const color = {
              'Click':        'var(--blue)',
              'Payme':        'var(--purple)',
              'Terminal':     'var(--cyan)',
              'Naqd':         'var(--green)',
              'Naqd (admin)': 'var(--green)',
              'Uzum':         'var(--yellow)',
              'Apelsin':      'var(--orange)',
              'Anor':         'var(--red)',
            }[s.source] || 'var(--text2)';
            return (
              <div key={i} style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{s.source}</span>
                  <span style={{ fontSize: 13, color: 'var(--green)', fontWeight: 700 }}>{fmt(s.revenue)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, height: 4, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${pctVal}%`, height: '100%', background: color, borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text3)', minWidth: 36 }}>{pct(pctVal)}</span>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>{fmtN(s.count)} ta</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bar foyda breakdown */}
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 11,
            color: 'var(--text2)', letterSpacing: 1.5, textTransform: 'uppercase' }}>
            🍕 Bar foydasi — mahsulot bo'yicha
          </div>
          {/* Bar jami qatori */}
          <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border)',
            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, background: 'rgba(255,193,7,0.05)' }}>
            <div style={{ fontSize: 11, color: 'var(--text2)' }}>
              <div style={{ color: 'var(--yellow)', fontWeight: 700, fontSize: 13 }}>{fmt(snack.revenue)}</div>
              <div>{tr('totalSales')}</div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text2)' }}>
              <div style={{ color: 'var(--red)', fontWeight: 700, fontSize: 13 }}>{fmt(snack.cost)}</div>
              <div>{tr('totalCost')}</div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text2)' }}>
              <div style={{ color: 'var(--green)', fontWeight: 700, fontSize: 13 }}>{fmt(snack.profit)}</div>
              <div>{tr('margin')} ({pct(snack.margin_pct)})</div>
            </div>
          </div>
          {!snack.by_product?.length ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)' }}>{tr('noSnackSales')}</div>
          ) : snack.by_product.map((p, i) => {
            const margin = p.revenue > 0 ? Math.round(p.profit / p.revenue * 100) : 0;
            return (
              <div key={i} style={{ padding: '9px 20px', borderBottom: '1px solid var(--border)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{p.product_name}</span>
                  <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8 }}>{p.qty_sold} ta</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: 'var(--green)', fontWeight: 700 }}>+{fmt(p.profit)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>savdo {fmt(p.revenue)} · {margin}%</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sessiyalar — type bo'yicha ajratilgan */}
      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)',
        borderRadius: 14, padding: '16px 20px' }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 12 }}>
          🎮 Sessiyalar (turi bo'yicha)
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
          PC — balans sarfi (topupda hisoblangan). Bilyard/PS — naqd daromad (yuqoridagi jamiga qo'shilgan).
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {(session.pc ?? session.total) > 0 && (
            <div style={{ borderLeft: '3px solid var(--cyan)', paddingLeft: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>🖥 PC (balans sarfi)</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text2)' }}>{fmt(session.pc ?? session.total)}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>daromad emas</div>
            </div>
          )}
          {session.billiard > 0 && (
            <div style={{ borderLeft: '3px solid var(--orange)', paddingLeft: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>🎱 Bilyard</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--green)' }}>{fmt(session.billiard)}</div>
              <div style={{ fontSize: 10, color: 'var(--green)', marginTop: 2 }}>naqd daromad</div>
            </div>
          )}
          {session.playstation > 0 && (
            <div style={{ borderLeft: '3px solid var(--purple)', paddingLeft: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>🎮 PlayStation</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--green)' }}>{fmt(session.playstation)}</div>
              <div style={{ fontSize: 10, color: 'var(--green)', marginTop: 2 }}>naqd daromad</div>
            </div>
          )}
        </div>
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'flex', gap: 24, fontSize: 11, color: 'var(--text3)' }}>
          <div>Jami sessiyalar: <b style={{ color: 'var(--text2)' }}>{session.count || 0}</b></div>
          <div>Balansdan: <b style={{ color: 'var(--text2)' }}>{fmt(session.balance)}</b></div>
          <div>Naqd/karta: <b style={{ color: 'var(--text2)' }}>{fmt(session.cash)}</b></div>
        </div>
      </div>
    </div>
  );
}

// ── SMENALAR TAB ──────────────────────────────────────────────────────────
function ShiftsTab() {
  const { t: tr } = useLang();
  const now = new Date();
  const [from, setFrom] = useState(new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString('sv-SE'));
  const [to, setTo]     = useState(now.toLocaleDateString('sv-SE'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/finance/shifts?from=${from}&to=${to}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const fmtTime = ts => ts ? new Date(ts).toLocaleString('uz-UZ', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—';
  const fmtDur  = h => { const hh = Math.floor(h || 0); const mm = Math.round(((h||0) - hh) * 60); return `${hh}s ${mm}d`; };

  const t = data?.totals;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <PeriodPicker from={from} to={to} setFrom={setFrom} setTo={setTo} />
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{tr('loading')}</div>
      ) : !data ? null : (
        <>
          {/* Jami kartalar */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
            <StatCard icon="🗒"  value={t.shifts_count}         label={tr('shiftHistory')}    color="var(--cyan)" />
            <StatCard icon="💰"  value={fmt(t.total_revenue)}   label={tr('totalRevenue')}    color="var(--green)" />
            <StatCard icon="🖥️"  value={fmtN(t.session_count)} label={tr('totalSessions')}   color="var(--purple)" />
            <StatCard icon="👤"  value={t.new_clients_count}    label={tr('newClientsLabel')} color="var(--orange)" />
          </div>

          {/* To'lov usuli jami — sessiya + snack + balans to'ldirish */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
            {[
              { icon: '💵', label: tr('cashLabel'),    color: 'var(--green)',  val2: (t.session_cash || 0) + (t.snack_cash || 0) + (t.topup_cash || 0) },
              { icon: '🔵', label: 'Click',            color: 'var(--blue)',   val2: (t.session_click || 0) + (t.snack_click || 0) + (t.topup_click || 0) },
              { icon: '📱', label: 'Payme',            color: 'var(--purple)', val2: (t.session_payme || 0) + (t.snack_payme || 0) + (t.topup_payme || 0) },
              { icon: '💳', label: 'Terminal',         color: 'var(--cyan)',   val2: (t.session_terminal || 0) + (t.snack_terminal || 0) + (t.topup_terminal || 0) },
              { icon: '🏦', label: tr('balanceLabel'), color: 'var(--yellow)', val2: t.session_balance },
            ].map(({ icon, label, color, val2 }) => (
              <div key={label} style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ fontSize: 18, marginBottom: 6 }}>{icon}</div>
                <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 14, fontWeight: 700, color }}>{fmt(val2)}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Smenalar jadvali */}
          <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
            <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', fontSize: 11,
              color: 'var(--text2)', letterSpacing: 1.5, textTransform: 'uppercase' }}>
              {tr('shiftHistory')} ({data.shifts.length})
            </div>
            {data.shifts.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)' }}>{tr('noShifts')}</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--card)' }}>
                      {[tr('operator'), tr('opened'), tr('closed'), tr('duration2'), tr('sessionsLabel'), tr('cashLabel'), 'Click', 'Terminal', 'Payme', 'Snack', tr('newClientsLabel'), tr('total')].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.shifts.map(s => {
                      const cashSum     = Number(s.session_cash || 0)     + Number(s.snack_cash || 0)     + Number(s.topup_cash || 0);
                      const clickSum    = Number(s.session_click || 0)    + Number(s.snack_click || 0)    + Number(s.topup_click || 0);
                      const terminalSum = Number(s.session_terminal || 0) + Number(s.snack_terminal || 0) + Number(s.topup_terminal || 0);
                      const paymeSum    = Number(s.session_payme || 0)    + Number(s.snack_payme || 0)    + Number(s.topup_payme || 0);
                      return (
                        <tr key={s.id} style={{ borderBottom: '1px solid var(--border)', opacity: s.closed_at ? 1 : 0.7 }}>
                          <td style={{ padding: '10px 14px', fontWeight: 600 }}>{s.operator_name}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{fmtTime(s.opened_at)}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{s.closed_at ? fmtTime(s.closed_at) : <span style={{ color: 'var(--green)', fontSize: 11 }}>● {tr('openTag')}</span>}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--text3)' }}>{fmtDur(s.duration_hours)}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--cyan)', fontWeight: 600 }}>{s.session_count || 0}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--green)' }}>{fmtN(cashSum)}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--blue)' }}>{fmtN(clickSum)}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--cyan)' }}>{fmtN(terminalSum)}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--purple)' }}>{fmtN(paymeSum)}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--yellow)' }}>{fmtN(s.snack_revenue)}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--orange)', textAlign: 'center' }}>{s.new_clients_count || 0}</td>
                          <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--green)', fontFamily: 'Orbitron, sans-serif', fontSize: 11 }}>{fmtN(s.total_revenue)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── P&L TAB ───────────────────────────────────────────────────────────────
function PLTab() {
  const { t: tr } = useLang();
  const now = new Date();
  const [from, setFrom] = useState(new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString('sv-SE'));
  const [to, setTo]     = useState(now.toLocaleDateString('sv-SE'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/finance/pl?from=${from}&to=${to}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{tr('loading')}</div>;

  const prev = data?.prev_period;
  const isProfit = (data?.profit || 0) >= 0;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <PeriodPicker from={from} to={to} setFrom={setFrom} setTo={setTo} />
      </div>

      {/* Asosiy kartalar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 12 }}>
        <StatCard icon="📈" value={fmt(data?.revenue)} label={tr('revenue')} color="var(--green)"
          sub="Topup + Bar foyda + Bilyard/PS" />
        <StatCard icon="📉" value={fmt(data?.expenses)} label={tr('expenses')} color="var(--red)" />
        <StatCard icon={isProfit ? '✅' : '❌'} value={fmt(data?.profit)} label={tr('profit')}
          color={isProfit ? 'var(--green)' : 'var(--red)'} />
        <StatCard icon="📊" value={pct(data?.margin)} label={`${tr('margin')} %`} color="var(--cyan)" />
      </div>

      {/* Daromad tarkibi */}
      <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 12,
        padding: '12px 20px', marginBottom: 12, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12 }}>
        <div><span style={{ color: 'var(--text3)' }}>💳 Topup (sof):</span> <b style={{ color: 'var(--cyan)' }}>{fmt(data?.topup)}</b></div>
        <div><span style={{ color: 'var(--text3)' }}>🍕 Bar foyda:</span> <b style={{ color: 'var(--yellow)' }}>{fmt(data?.snack_profit)}</b></div>
        {(data?.timed_revenue || 0) > 0 && (
          <div><span style={{ color: 'var(--text3)' }}>🎱🎮 Bilyard/PS:</span> <b style={{ color: 'var(--orange)' }}>{fmt(data?.timed_revenue)}</b></div>
        )}
      </div>

      {/* Bar vazvrat (agar bor bo'lsa) */}
      {Number(data?.snack_returns_amount || 0) > 0 && (
        <div style={{
          background: 'rgba(255,193,7,0.06)', border: '1px solid rgba(255,193,7,0.3)',
          borderRadius: 12, padding: '10px 18px', marginBottom: 28,
          display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12, alignItems: 'center',
        }}>
          <span style={{ color: 'var(--text3)' }}>↩ Bar vazvrat:</span>
          <b style={{ color: 'var(--red)' }}>−{fmt(data.snack_returns_amount)}</b>
          <span style={{ color: 'var(--text3)' }}>·</span>
          <span style={{ color: 'var(--text3)' }}>Foyda ta'siri:</span>
          <b style={{ color: 'var(--red)' }}>−{fmt(Math.max(0, (data.snack_returns_amount || 0) - (data.snack_returns_cost || 0)))}</b>
          <span style={{ color: 'var(--text3)', fontSize: 11 }}>(daromad − tannarx qaytdi)</span>
        </div>
      )}

      {/* Xarajat breakdown */}
      <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 11,
          color: 'var(--text2)', letterSpacing: 1.5, textTransform: 'uppercase' }}>Xarajatlar (kategoriya bo'yicha)</div>
        {!data?.expense_breakdown?.length ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
            Xarajat yo'q. <a href="/expenses" style={{ color: 'var(--cyan)' }}>Qo'shish →</a>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--card)' }}>
                {[tr('type'), tr('amount'), '%'].map(h => (
                  <th key={h} style={{ padding: '10px 20px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.expense_breakdown.map((e, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 20px', fontWeight: 600 }}>{e.category}</td>
                  <td style={{ padding: '10px 20px', color: 'var(--red)', fontWeight: 700 }}>{fmt(e.amount)}</td>
                  <td style={{ padding: '10px 20px', color: 'var(--text2)' }}>
                    {data.expenses > 0 ? pct(e.amount / data.expenses * 100) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Balans majburiyati */}
      {data?.liability !== undefined && (
        <div style={{ background: 'rgba(255,193,7,0.06)', border: '1px solid rgba(255,193,7,0.3)',
          borderRadius: 14, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ fontSize: 32 }}>⚖️</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--yellow)' }}>Balans majburiyati</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 3 }}>
              Foydalanuvchilar balansi yig'indisi: <b style={{ color: 'var(--text)' }}>{fmt(data.liability?.total_liability)}</b>
              {' · '}{data.liability?.users_with_balance} foydalanuvchi
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── MIJOZLAR TAB ──────────────────────────────────────────────────────────
function CustomersTab() {
  const { t: tr } = useLang();
  const now = new Date();
  const [from, setFrom] = useState(new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString('sv-SE'));
  const [to, setTo]     = useState(now.toLocaleDateString('sv-SE'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/finance/customers?from=${from}&to=${to}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{tr('loading')}</div>;
  if (!data) return null;

  const nvr = data.new_vs_return || {};

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <PeriodPicker from={from} to={to} setFrom={setFrom} setTo={setTo} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
        <StatCard icon="💰" value={fmt(data.arpu)} label="ARPU" color="var(--green)" />
        <StatCard icon="📊" value={fmt(data.avg_session_value)} label={tr('avgSession')} color="var(--cyan)" />
        <StatCard icon="⏱️" value={`${data.avg_duration_min || 0} ${tr('minsShort')}`} label={tr('elapsedLabel')} color="var(--purple)" />
        <StatCard icon="👥" value={data.unique_users || 0} label={tr('totalSessions')} color="var(--orange)" />
      </div>

      {/* Yangi vs qaytgan */}
      <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14,
        padding: '16px 20px', marginBottom: 20, display: 'flex', gap: 32 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--green)' }}>{nvr.new_users || 0}</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>🆕 Yangi mijozlar</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--cyan)' }}>{nvr.returning_users || 0}</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>🔄 Qaytgan mijozlar</div>
        </div>
        {(nvr.new_users + nvr.returning_users) > 0 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--purple)' }}>
              {(() => { const t = (nvr.new_users||0)+(nvr.returning_users||0); return t > 0 ? Math.round((nvr.returning_users||0)/t*100) : 0; })()}%
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>🎯 Qaytish darajasi</div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Top mijozlar */}
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 11,
            color: 'var(--text2)', letterSpacing: 1.5, textTransform: 'uppercase' }}>🏆 Top 10 mijoz</div>
          {(data.top_customers?.length === 0) && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Ma'lumot yo'q</div>}
          {data.top_customers?.map((u, i) => (
            <div key={u.id} style={{ padding: '11px 20px', borderBottom: '1px solid var(--border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, color: 'var(--text3)', minWidth: 20 }}>#{i + 1}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{u.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{u.phone} · {u.session_count} sessiya</div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--green)' }}>{fmt(u.total_spent)}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{fmtN(u.loyalty_points)} ball</div>
              </div>
            </div>
          ))}
        </div>

        {/* Nofaol mijozlar */}
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 11,
            color: 'var(--text2)', letterSpacing: 1.5, textTransform: 'uppercase' }}>😴 14+ kun kelmagan</div>
          {data.inactive_customers?.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Yaxshi! Hammasi faol</div>
          ) : data.inactive_customers?.map(u => (
            <div key={u.id} style={{ padding: '11px 20px', borderBottom: '1px solid var(--border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{u.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{u.phone}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: 'var(--red)' }}>
                  {u.last_session ? `${Math.floor((Date.now() - new Date(u.last_session)) / 86400000)} kun oldin` : '—'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{fmtN(u.balance)} so'm</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── BANDLIK TAB ───────────────────────────────────────────────────────────
function OccupancyTab() {
  const { t: tr } = useLang();
  const now = new Date();
  const [from, setFrom] = useState(new Date(now.getTime() - 7 * 86400000).toLocaleDateString('sv-SE'));
  const [to, setTo]     = useState(now.toLocaleDateString('sv-SE'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/finance/occupancy?from=${from}&to=${to}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  // Heatmap: 7 kun × 24 soat
  const DAYS = ['Yak', 'Dush', 'Sesh', 'Chor', 'Pay', 'Juma', 'Shan'];
  const heatmapGrid = Array(7).fill(0).map((_, d) =>
    Array(24).fill(0).map((_, h) => {
      const cell = data?.heatmap?.find(r => parseInt(r.dow) === d && parseInt(r.hour) === h);
      return cell ? parseInt(cell.sessions) : 0;
    })
  );
  const maxSessions = Math.max(1, ...heatmapGrid.flat());

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{tr('loading')}</div>;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <PeriodPicker from={from} to={to} setFrom={setFrom} setTo={setTo} />
      </div>

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
            <StatCard icon="🖥️" value={`${data.summary?.utilization_pct || 0}%`} label={tr('activeSessions')} color="var(--cyan)" />
            <StatCard icon="⏱️" value={`${data.summary?.used_hours || 0}h`} label={tr('elapsedLabel')} color="var(--green)" />
            <StatCard icon="💸" value={fmt(data.summary?.lost_revenue)} label={tr('profitLoss')} color="var(--red)" />
            <StatCard icon="🖥️" value={data.summary?.pc_count || 0} label={tr('computersTitle').replace('🖥️ ', '')} color="var(--purple)" />
          </div>

          {/* Heatmap */}
          <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14,
            padding: 20, marginBottom: 20, overflowX: 'auto' }}>
            <div style={{ fontSize: 11, color: 'var(--text2)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 16 }}>
              Bandlik xaritasi (soat × kun)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '40px repeat(24, 1fr)', gap: 2, minWidth: 700 }}>
              <div />
              {Array(24).fill(0).map((_, h) => (
                <div key={h} style={{ fontSize: 9, color: 'var(--text3)', textAlign: 'center' }}>{h}</div>
              ))}
              {heatmapGrid.map((row, d) => (
                <React.Fragment key={d}>
                  <div style={{ fontSize: 10, color: 'var(--text2)', alignSelf: 'center' }}>{DAYS[d]}</div>
                  {row.map((v, h) => {
                    const intensity = v / maxSessions;
                    const alpha = 0.1 + intensity * 0.9;
                    return (
                      <div key={h} title={`${DAYS[d]} ${h}:00 — ${v} sessiya`} style={{
                        height: 18, borderRadius: 3,
                        background: v > 0 ? `rgba(0,212,255,${alpha})` : 'var(--border)',
                        cursor: 'default',
                      }} />
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* PC bo'yicha */}
          <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 11,
              color: 'var(--text2)', letterSpacing: 1.5, textTransform: 'uppercase' }}>Kompyuter bo'yicha</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--card)' }}>
                  {['PC', tr('type'), tr('sessionsLabel'), tr('revenue'), tr('hoursShort')].map(h => (
                    <th key={h} style={{ padding: '10px 20px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.by_computer?.filter(c => parseInt(c.sessions) > 0).length === 0 && (
                  <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>{tr('noSessions')}</td></tr>
                )}
                {data.by_computer?.filter(c => parseInt(c.sessions) > 0).map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 20px', fontWeight: 600 }}>#{c.number}</td>
                    <td style={{ padding: '10px 20px', color: 'var(--text2)' }}>{c.type}</td>
                    <td style={{ padding: '10px 20px' }}>{c.sessions}</td>
                    <td style={{ padding: '10px 20px', color: 'var(--green)', fontWeight: 700 }}>{fmt(c.revenue)}</td>
                    <td style={{ padding: '10px 20px', color: 'var(--text2)' }}>{Math.round(c.total_hours * 10) / 10}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── NAZORAT TAB ───────────────────────────────────────────────────────────
function AnomaliesTab() {
  const { t: tr } = useLang();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/finance/anomalies').then(r => setAlerts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const COLORS = { danger: 'var(--red)', warning: 'var(--orange)', info: 'var(--cyan)' };
  const ICONS  = { danger: '🚨', warning: '⚠️', info: 'ℹ️' };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{tr('loading')}</div>;

  return (
    <div>
      <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text2)' }}>
        {tr('suspiciousActivity')}
      </div>
      {alerts.length === 0 ? (
        <div style={{ background: 'rgba(0,255,136,0.05)', border: '1px solid var(--green)',
          borderRadius: 14, padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--green)' }}>{tr('noAnomalies')}</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 6 }}>{tr('normal')}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {alerts.map((a, i) => (
            <div key={i} style={{
              background: COLORS[a.level] + '0d',
              border: `1px solid ${COLORS[a.level]}40`,
              borderRadius: 12, padding: '16px 20px',
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <span style={{ fontSize: 20 }}>{ICONS[a.level]}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLORS[a.level] }}>{a.message}</div>
                {a.type && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Tur: {a.type}</div>}
                {a.date && <div style={{ fontSize: 11, color: 'var(--text3)' }}>Sana: {new Date(a.date).toLocaleDateString('uz-UZ')}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── ASOSIY SAHIFA ─────────────────────────────────────────────────────────
export default function Finance() {
  const { t: tr } = useLang();
  const [activeTab, setActiveTab] = useState('revenue');

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{tr('financeTitle')}</h1>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>{tr('revenue')}, {tr('expenses')}, P&L</div>
      </div>

      {/* Tablar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
        {getTabs(tr).map(tab => (
          <Tab key={tab.key} active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)} label={tab.label} />
        ))}
      </div>

      {activeTab === 'revenue'   && <RevenueTab />}
      {activeTab === 'shifts'    && <ShiftsTab />}
      {activeTab === 'pl'        && <PLTab />}
      {activeTab === 'customers' && <CustomersTab />}
      {activeTab === 'occupancy' && <OccupancyTab />}
      {activeTab === 'anomalies' && <AnomaliesTab />}
    </div>
  );
}
